import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { selectHasFeatureFlag } from '@amfam/shared/utility/feature-flag/data-access/lib/+state/feature-flag.selectors';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import {
  ROUTE_PARAMS,
  SESSION_STORAGE_KEYS
} from '@amfam/shared/utility/shared-services/lib/storage/storage-keys';
import { ComponentViewService } from '@amfam/ui-kit/lib/shared-components/component-view.service';

import { selectBrandState } from '../+state/brand.selectors';
import { BrandModel } from '../models/brand.model';
import { BrandService } from '../services/brand.service';
import {
  HOMESITE_SMALL_PARTNERS,
  MSA_SMALL_PARTNERS,
  PARTNER_ID_BY_EXPERIENCE_ID
} from '../services/partner-mappings';

// Default partner id based on build, myaccount-admin will default to 642.
export const DEFAULT_PARTNER_ID = env.myAccountBuildGroupId || '642';
const HOMESITE_PARTNER_STORAGE_KEY = SESSION_STORAGE_KEYS.HOMESITE_PARTNER_ID_BRAND;
const MSA_PARTNER_STORAGE_KEY = SESSION_STORAGE_KEYS.MSA_PARTNER_ID_BRAND;

// Helper Functions
function isNumeric(value: string): boolean {
  return !isNaN(Number(value)) && isFinite(Number(value));
}

function getPartnerIdFromBuild(): string | null {
  return env.myAccountBuildGroupId;
}

function getPartnerIdFromExperience(experienceId: string): string | null {
  for (const [partnerId, experienceIds] of Object.entries(PARTNER_ID_BY_EXPERIENCE_ID)) {
    if (Array.isArray(experienceIds) && experienceIds.includes(experienceId)) {
      return partnerId;
    }
  }
  return null;
}

function getPartnerIdFromSmallPartners(
  context: string,
  partners: Array<{ key: string; value: number }>
): string | null {
  return partners.find(p => p.key === context)?.value.toString() ?? null;
}

function resolvePartnerId(context: string | null): string {
  if (!context) {
    const urlBasedId = getPartnerIdFromBuild();
    if (urlBasedId) {
      sessionStorage.setItem(HOMESITE_PARTNER_STORAGE_KEY, 'true');
      return urlBasedId;
    }
    return DEFAULT_PARTNER_ID;
  }

  // Check if context is a numeric experience ID
  if (isNumeric(context)) {
    const experienceBasedId = getPartnerIdFromExperience(context);
    if (experienceBasedId) {
      return experienceBasedId;
    }
  }

  // Check homesite partner list by context key
  const homesiteId = getPartnerIdFromSmallPartners(context, HOMESITE_SMALL_PARTNERS);
  if (homesiteId) {
    sessionStorage.setItem(HOMESITE_PARTNER_STORAGE_KEY, 'true');
    return homesiteId;
  }

  // Check MSA partner list by context key
  const msaId = getPartnerIdFromSmallPartners(context, MSA_SMALL_PARTNERS);
  if (msaId) {
    sessionStorage.setItem(MSA_PARTNER_STORAGE_KEY, 'true');
    return msaId;
  }

  return DEFAULT_PARTNER_ID;
}

function getPartnerIdInWebview(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  componentViewService: ComponentViewService
): string | null {
  if (componentViewService.isInWebview(state.url) && route.queryParamMap.has('customerGroupId')) {
    return route.queryParamMap.get('customerGroupId');
  }
  return null;
}

function getExperienceContext(route: ActivatedRouteSnapshot): string | null {
  const expidFromRoute = route.root.firstChild?.firstChild?.paramMap.get(
    ROUTE_PARAMS.EXPERIENCE_ID
  );
  if (expidFromRoute) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.EXPERIENCE_ID, expidFromRoute);
    return expidFromRoute;
  }

  return sessionStorage.getItem(SESSION_STORAGE_KEYS.EXPERIENCE_ID);
}

function loadBrand(
  partnerId: string,
  store: Store,
  brandService: BrandService
): Observable<boolean> {
  const brand: BrandModel = store.selectSignal(selectBrandState)();
  if (brand.loaded) {
    return of(true);
  }

  return brandService.loadBrandForUrl(partnerId).pipe(
    take(1),
    map(() => true),
    catchError(() => {
      store.dispatch(fromRouterActions.Go({ path: ['/not-found'] }));
      return of(false);
    })
  );
}

// Functional Guard
export const brandGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const store = inject(Store);
  const brandService = inject(BrandService);
  const appService = inject(ApplicationService);
  const componentViewService = inject(ComponentViewService);

  return store.select(selectHasFeatureFlag).pipe(
    filter(loaded => loaded),
    take(1),
    switchMap(() => {
      let partnerId: string;

      // Admin flow
      if (appService.isApp(Applications.MYACCOUNT_ADMIN)) {
        partnerId = sessionStorage.getItem(SESSION_STORAGE_KEYS.PARTNER_ID) ?? DEFAULT_PARTNER_ID;
        return loadBrand(partnerId, store, brandService);
      }

      // Webview flow
      const webviewPartnerId = getPartnerIdInWebview(route, state, componentViewService);
      if (webviewPartnerId) {
        return loadBrand(webviewPartnerId, store, brandService);
      }

      // MyAccount flow
      const context = getExperienceContext(route);
      partnerId = resolvePartnerId(context);
      return loadBrand(partnerId, store, brandService);
    })
  );
};
